import {
    FOCUS_RING_NO_RADIUS,
    Pills,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useRef, useState, type ReactNode } from 'react';

import { FeedbackPrivacyPill } from './FeedbackPrivacyPill';

import {
    ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
    isAnonymousFeedback,
    type FeedbackPrivacyValue,
} from '@/constants/review/FeedbackPrivacy';
import { TOOLTIP_DELAY_MS } from '@/constants/Tooltip';
import { useAmelioTranslations } from '@/hooks';

interface FeedbackSettingPillProps {
    /** The pill itself, or any other mark of the setting. */
    children: ReactNode;
    /** One sentence per line: what the setting means for the people who answer and read. */
    explanation: string[];
    dataTestId: string;
    /** The surface that answers to hover, focus and tap. Default: a pill. */
    className?: string;
}

/**
 * Focusable so that a keyboard reaches the explanation a mouse reads on hover. A touch screen has no hover, so a
 * tap opens it too; a tap elsewhere closes it.
 */
export const FeedbackSettingPill = ({
    children,
    explanation,
    dataTestId,
    className = 'inline-flex rounded-full whitespace-nowrap',
}: FeedbackSettingPillProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const pointerTypeRef = useRef<string | undefined>(undefined);

    return (
        <Tooltip
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <TooltipTrigger asChild>
                <span
                    tabIndex={0}
                    className={`${FOCUS_RING_NO_RADIUS} ${className}`}
                    data-testid={dataTestId}
                    onPointerDown={(event) => {
                        pointerTypeRef.current = event.pointerType;
                    }}
                    onClick={(event) => {
                        if (pointerTypeRef.current !== 'touch') {
                            return;
                        }
                        // The tooltip closes on a click; a tap is how a touch screen asks for it, so it opens instead,
                        // and does not also reach a card the mark sits in.
                        event.preventDefault();
                        event.stopPropagation();
                        setIsOpen(true);
                    }}
                >
                    {children}
                </span>
            </TooltipTrigger>

            <TooltipContent
                side={'bottom'}
                className={'flex max-w-xs flex-col gap-1'}
                data-testid={`${dataTestId}-explanation`}
            >
                {explanation.map((sentence) => (
                    <p key={sentence}>{sentence}</p>
                ))}
            </TooltipContent>
        </Tooltip>
    );
};

export interface FeedbackSettingsPillsProps {
    /** Absent for an evaluation that is not a 360. */
    feedbackPrivacy?: FeedbackPrivacyValue | null;
    /** Only a 360 whose comments take no reply is labelled: replies are what a 360 has by default. */
    commentRepliesEnabled?: boolean | null;
    /** The server's, where a response gives it; a wizard, which has none, states the one a 360 is launched with. */
    minimumResponses?: number;
    className?: string;
    dataTestId?: string;
}

/**
 * The settings of a 360 that decide what its results show, each as a label explained on hover and
 * on focus. Every screen that names them (launch summaries, results) says them through this one.
 */
export const FeedbackSettingsPills = ({
    feedbackPrivacy,
    commentRepliesEnabled,
    minimumResponses = ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
    className = '',
    dataTestId = 'feedback-settings',
}: FeedbackSettingsPillsProps) => {
    const { t } = useAmelioTranslations();
    const areRepliesDisabled = commentRepliesEnabled === false;

    if (!feedbackPrivacy && !areRepliesDisabled) {
        return null;
    }

    const isAnonymous = isAnonymousFeedback(feedbackPrivacy);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
            <div className={`flex flex-wrap gap-2 ${className}`}>
                {feedbackPrivacy && (
                    <FeedbackSettingPill
                        dataTestId={`${dataTestId}-privacy`}
                        explanation={
                            isAnonymous
                                ? [
                                      t(
                                          'evaluation360ScheduleCreation.resultsVisibility.noIndividualAnswer',
                                          { minimum: minimumResponses },
                                      ),
                                  ]
                                : [t('feedbackPrivacy.namedExplanation')]
                        }
                    >
                        <FeedbackPrivacyPill feedbackPrivacy={feedbackPrivacy} />
                    </FeedbackSettingPill>
                )}

                {areRepliesDisabled && (
                    <FeedbackSettingPill
                        dataTestId={`${dataTestId}-comments`}
                        explanation={[t('feedbackComments.pillExplanation')]}
                    >
                        <Pills
                            variant={'lightPurple'}
                            size={'sm'}
                        >
                            {t('feedbackComments.pill')}
                        </Pills>
                    </FeedbackSettingPill>
                )}
            </div>
        </TooltipProvider>
    );
};
